import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { PhysicalInventoryCountEnums } from 'src/app/enum/physical-inventory-count-enums';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ModalObjectType } from 'src/app/interfaces/modal-object-types.interface';
import { PhysicalInventoryDetailModel } from 'src/app/models/inventory/physical-inventory-detail.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { CountTableModalModel } from 'src/app/models/tables/inventory/count-table-modal.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { PhysicalInventoryDetailService } from 'src/app/services/inventory/physical-inventory-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-physical-inventory-detail',
  templateUrl: './register-physical-inventory-detail.component.html',
  styleUrls: ['./register-physical-inventory-detail.component.scss'],
  standalone: false,
})
export class RegisterPhysicalInventoryDetailComponent implements OnInit {

  private readonly articleService = inject(ArticleService);
  private readonly config = inject(DynamicDialogConfig);
  private readonly formBuilder = inject(FormBuilder);
  private readonly physicalInventoryDetailService = inject(PhysicalInventoryDetailService);
  private readonly ref = inject(DynamicDialogRef);
  private readonly toastHandlerService = inject(ToastHandlerService);

  articleId: number = 0;
  ascendant: boolean = true;
  changeCount: boolean = false;
  columnSort: string | null = null;
  count: number = 0;
  countData: PagedResponseModel<CountTableModalModel> = new PagedResponseModel<CountTableModalModel>();
  codeNameArticle: string = '';
  formPhysicalInventoryDetail: FormGroup = new FormGroup({});
  header: ColumnResponseModel[] = [];
  hiddenCount1: boolean = true;
  hiddenCount2: boolean = true;
  hiddenCount3: boolean = true;
  hiddenCountFinal: boolean = true;
  limit: number = 10;
  page: number = 1;
  physicalInventoryDetail: PhysicalInventoryDetailModel = new PhysicalInventoryDetailModel();
  physicalInventoryDetailId: number = 0;
  permissionUpdatable: boolean = false;
  physicalInventoryId: number = 0;
  suscription: Subscription;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }
  ];

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.suscription = input$?.subscribe((inputs: any) => {
      this.physicalInventoryId = inputs.physicalInventoryId;
      this.permissionUpdatable = inputs.permissionUpdatable;
      this.count = inputs.count;
    });
    this.validateForm(this.physicalInventoryDetail);
    await this.getColumns();
    await this.getAllArticleCountModalWithPagination();
    this.updateVisibleCount(this.count as PhysicalInventoryCountEnums);
  }

  ngOnDestroy() {
    this.suscription?.unsubscribe();
  }

  async changeArticle(articleCode: number | string | null) {
    const parsedArticleCode = Number(articleCode);

    if (articleCode === '' || !Number.isFinite(parsedArticleCode) || parsedArticleCode < 0) {
      this.clearForm();
      return;
    }

    try {
      const article = await firstValueFrom(
        this.physicalInventoryDetailService.getArticleByCodeAsync(this.physicalInventoryId, parsedArticleCode)
      );
      if (article) {
        this.articleId = article.articleId;
        this.codeNameArticle = article.name;
        this.formPhysicalInventoryDetail.controls['articleId'].patchValue(this.articleId);
        this.formPhysicalInventoryDetail.controls['articleCode'].patchValue(article.code);
        await this.getPhysicalInventoryDetailByKeys();
      }
    } catch (error) {
      this.articleId = 0;
      this.formPhysicalInventoryDetail.controls['articleId'].patchValue(0);
      this.codeNameArticle = '';
      console.error('Error al buscar el artículo por código.', error);
    }
  }

  clearForm() {
    this.codeNameArticle = '';
    this.physicalInventoryDetail = new PhysicalInventoryDetailModel();
    this.physicalInventoryDetailId = 0;
    this.articleId = 0;
    this.validateForm(this.physicalInventoryDetail);
    this.formPhysicalInventoryDetail.controls['articleId'].patchValue(0);
  }

  close() {
    const data: ModalObjectType = {};
    if (this.changeCount) {
      data.params = { reload: true };
      this.ref.close(data);
    } else {
      data.params = { reload: false };
      this.ref.close(data);
    }
  }

  async getAllArticleCountModalWithPagination() {
    try {
      this.countData = await firstValueFrom(
        this.physicalInventoryDetailService.getArticleCountModalWithPagination(
          this.page, this.limit, this.columnSort, this.ascendant, this.physicalInventoryId, this.count
        ));
    } catch (error) {
      console.error('Error al obtener los artículos para el modal de conteo.', error);
    }
  }

  async getArticle(articleId: number | null) {
    if (articleId !== null && articleId !== 0) {
      const article = await firstValueFrom(this.articleService.getArticleById(articleId));
      if (article) {
        this.codeNameArticle = article.name;
        this.formPhysicalInventoryDetail.controls["articleId"].patchValue(articleId);
        this.formPhysicalInventoryDetail.controls["articleCode"].patchValue(article.code);
        this.articleId = articleId;
      } else {
        this.articleId = 0;
        this.formPhysicalInventoryDetail.controls["articleId"].patchValue(0);
        this.formPhysicalInventoryDetail.controls["articleCode"].patchValue(null);
        this.codeNameArticle = '';
        return;
      }
    } else {
      this.articleId = 0;
      this.formPhysicalInventoryDetail.controls["articleId"].patchValue(0);
      this.formPhysicalInventoryDetail.controls["articleCode"].patchValue(null);
      this.codeNameArticle = "";
    }
  }

  async getColumns() {
    try {
      this.header = await firstValueFrom(this.physicalInventoryDetailService.getColumnsForCountModal());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getPhysicalInventoryDetailByKeys() {
    if (this.articleId !== 0) {
      try {
        this.physicalInventoryDetail = await firstValueFrom(this.physicalInventoryDetailService.getPhysicalInventoryDetailByKeys(
          this.physicalInventoryId, this.articleId, this.count
        ));
        if (this.count === PhysicalInventoryCountEnums.count3) {
          if (!this.physicalInventoryDetail.swCount1) {
            this.toastHandlerService.showToast('warning', 'Este equipo no tiene 1er conteo.');
            this.clearForm();
            return;
          }
          if (!this.physicalInventoryDetail.swCount2) {
            this.toastHandlerService.showToast('warning', 'Este equipo no tiene 2do conteo.');
            this.clearForm();
            return;
          }
          if (this.physicalInventoryDetail.swFinalCount && !this.physicalInventoryDetail.swCount3) {
            this.toastHandlerService.showToast('info', 'Este equipo no requiere 3er conteo.');
            this.clearForm();
            return;
          }
        }
        this.physicalInventoryDetailId = this.physicalInventoryDetail.physicalInventoryDetailId;
        this.articleId = this.physicalInventoryDetail.articleId;
        this.validateForm(this.physicalInventoryDetail);
      } catch (error) {
        const modelPhysicalInventoryDetail = new PhysicalInventoryDetailModel();
        modelPhysicalInventoryDetail.articleId = this.articleId;
        this.physicalInventoryDetail = modelPhysicalInventoryDetail;
        this.validateForm(modelPhysicalInventoryDetail);
        console.error('Error al buscar el detalle del inventario físico por llaves.', error);
      }
    }
  }

  async onAction(event: { action: string, item?: CountTableModalModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      if (this.permissionUpdatable) {
        try {
          this.physicalInventoryDetail = await firstValueFrom(this.physicalInventoryDetailService.getPhysicalInventoryDetailById(event.item.id));
          this.physicalInventoryDetailId = this.physicalInventoryDetail.physicalInventoryDetailId;
          this.validateForm(this.physicalInventoryDetail);
        } catch (error) {
          console.error('Error al consultar el detalle del conteo por Id.', error);
        }
      } else {
        this.toastHandlerService.showToast(
          'error',
          'El usuario no tiene permisos para modificar detalles del inventario físico. Comuníquese con el administrador del sistema.'
        );
      }
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getAllArticleCountModalWithPagination();
  }

  async send() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para modificar detalles del inventario físico. Comuníquese con el administrador del sistema.');
      return;
    }
    Object.keys(this.formPhysicalInventoryDetail.controls).forEach(key => {
      const control = this.formPhysicalInventoryDetail.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formPhysicalInventoryDetail.valid) {
      const modelPhysicalInventoryDetail = this.formPhysicalInventoryDetail.getRawValue() as PhysicalInventoryDetailModel;

      try {
        this.physicalInventoryDetail.cardNumber = modelPhysicalInventoryDetail.cardNumber;
        this.physicalInventoryDetail.observation = modelPhysicalInventoryDetail.observation;

        switch (this.count) {
          case PhysicalInventoryCountEnums.count1:
            this.physicalInventoryDetail.physicalQuantity1 = modelPhysicalInventoryDetail.physicalQuantity1;
            break;
          case PhysicalInventoryCountEnums.count2:
            this.physicalInventoryDetail.physicalQuantity2 = modelPhysicalInventoryDetail.physicalQuantity2;
            break;
          case PhysicalInventoryCountEnums.count3:
            this.physicalInventoryDetail.physicalQuantity3 = modelPhysicalInventoryDetail.physicalQuantity3;
            break;
          default:
            break;
        }
        try {
          await firstValueFrom(this.physicalInventoryDetailService.updatePhysicalInventoryDetail(this.physicalInventoryDetail));
          this.toastHandlerService.showToast(
            'success',
            `El detalle del conteo ${this.count}, fue modificado exitosamente.`
          );
          this.changeCount = true;
          this.getAllArticleCountModalWithPagination();
          this.clearForm();
        } catch (error) {
          this.changeCount = true;
          this.getAllArticleCountModalWithPagination();
          console.error('Error al modificar el detalle del inventario físico.', error);
        }
      } catch (error) {
        console.error('Error al obtener las existencias por artículo y bodega.', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      articleId: {
        required: 'El campo Artículo es obligatorio.',
        min: 'Debe seleccionar un Artículo válido.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formPhysicalInventoryDetail.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  private updateVisibleCount(countEnum: PhysicalInventoryCountEnums | undefined) {
    this.hiddenCount1 = true;
    this.hiddenCount2 = true;
    this.hiddenCount3 = true;
    this.hiddenCountFinal = true;

    switch (countEnum) {
      case PhysicalInventoryCountEnums.count1:
        this.hiddenCount1 = false;
        break;
      case PhysicalInventoryCountEnums.count2:
        this.hiddenCount2 = false;
        break;
      case PhysicalInventoryCountEnums.count3:
        this.hiddenCount3 = false;
        break;
      default:
        break;
    }
  }

  validateForm(physicalInventoryDetail: PhysicalInventoryDetailModel) {
    this.formPhysicalInventoryDetail = this.formBuilder.group({
      articleId: [physicalInventoryDetail.articleId, [Validators.required, Validators.min(1)]],
      articleCode: [null],
      initialUnitCost: [{ value: physicalInventoryDetail.initialUnitCost ?? 0, disabled: true }],
      cardNumber: [physicalInventoryDetail.cardNumber],
      observation: [physicalInventoryDetail.observation],
      physicalQuantity1: [physicalInventoryDetail.physicalQuantity1, [Validators.required]],
      physicalQuantity2: [physicalInventoryDetail.physicalQuantity2, [Validators.required]],
      physicalQuantity3: [physicalInventoryDetail.physicalQuantity3, [Validators.required]],
      physicalQuantityFinal: [physicalInventoryDetail.physicalQuantityFinal, [Validators.required]]
    });
    this.updateVisibleCount(this.count as PhysicalInventoryCountEnums);
    if (physicalInventoryDetail.articleId !== 0) {
      this.getArticle(physicalInventoryDetail.articleId);
    }
  }

}
